import { useQuery, useMutation } from '@tanstack/react-query'
import { apiInstance } from 'services/api'

import type { OfferListResponse } from 'services/offer/hooks/useOfferList'
import type { BenefitsListResponse } from 'services/terms/terms.hooks'

export const useOpenOffersList = (token: string) => {
  const { hasUserConsented } = useOpenTermsOfUse(token)

  return useQuery({
    queryKey: ['openOffersList'],
    cacheTime: 0,
    queryFn: () =>
      apiInstance<OfferListResponse[]>({
        url: '/projects/offers/',
        method: 'GET',
        headers: {
          authorization: `Token ${token}`
        }
      }).then(({ data }) => data),
    enabled: !!token && hasUserConsented
  })
}

export const useOpenTermsOfUse = (token: string) => {
  const { data: termsOfUse, ...rest } = useQuery({
    queryKey: ['openTermsOfUse'],
    cacheTime: 3600000,
    staleTime: 3600000,
    queryFn: () =>
      apiInstance<BenefitsListResponse>({
        url: '/documents/terms-of-use/',
        method: 'GET',
        headers: {
          authorization: `Token ${token}`
        }
      }).then(({ data }) => data),
    enabled: !!token
  })

  const { mutateAsync: mutateAsyncTermsOfUse, isLoading: isLoadingTermsOfUse } = useMutation({
    cacheTime: 0,
    mutationFn: (id?: number) =>
      apiInstance<BenefitsListResponse>({
        url: `/documents/terms-of-use/`,
        method: 'POST',
        data: {
          id
        },
        headers: {
          authorization: `Token ${token}`
        }
      }).then(({ data }) => data)
  })

  const hasUserConsented = !!termsOfUse ? Boolean(termsOfUse.isAccepted) : false

  return {
    termsOfUse,
    hasUserConsented,
    mutateAsyncTermsOfUse,
    isLoadingTermsOfUse,
    ...rest
  }
}

export const useOpenOfferMessagesCreate = (token: string) => {
  const { mutateAsync, ...rest } = useMutation({
    mutationFn: ({ message, offerId }: { message: string; offerId: number }) => {
      const form = new FormData()

      form.append('message', message)

      return apiInstance({
        url: `/projects/offers/${offerId}/messages/`,
        method: 'POST',
        headers: {
          authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        data: form,
        transformRequest: (data) => {
          return data
        }
      })
    }
  })

  return { createMessage: mutateAsync, ...rest }
}
